/**
 * Direction 3 — Layered control plane primitives (Wave 0).
 * Task truth first; implementation truth under TechnicalDetails.
 * Frozen pages must not import these for restyle.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { html, M, SOFT } from "./ui.js";
import { RuntimeRail } from "./object-workbench.js";

/** Product status language — never Connected ⇒ Callable. */
export const READINESS_LABELS = Object.freeze({
  configured: "Configured",
  connected: "Connected",
  available: "Available",
  callable: "Callable",
  verified: "Verified",
});

/** Map legacy AI step ids onto the shared ladder. */
export function normalizeReadinessSteps(steps) {
  const rows = Array.isArray(steps) ? steps : [];
  return rows.map((s) => {
    const id = String(s.id || "");
    const mapped = id === "tool-listed" ? "available" : id;
    return {
      ...s,
      id: mapped,
      label: READINESS_LABELS[mapped] || s.label || mapped,
    };
  });
}

export function ReadinessState({ steps, next, label }) {
  const rows = normalizeReadinessSteps(steps);
  return html`<section className="layer-ready" aria-label=${label || "Readiness"}>
    <${RuntimeRail} steps=${rows} next=${next}/>
  </section>`;
}

export function TechnicalDetails({ title, children, open = false }) {
  return html`<details className="layer-tech" open=${open || undefined}>
    <summary>${title || "Proof & detail"}</summary>
    <div className="layer-tech__body">${children}</div>
  </details>`;
}

export function EmptyAction({ title, say, next, actionLabel, onAction, disabled }) {
  return html`<div className="layer-empty" role="status">
    <b>${title || "Nothing here yet"}</b>
    <p>${say || "Take the next step to continue."}</p>
    ${next ? html`<p className="layer-empty__next">${next}</p>` : null}
    ${actionLabel && onAction ? html`
      <button type="button" className="btn btn--sm btn--go" disabled=${!!disabled} onClick=${onAction}>
        ${actionLabel}
      </button>` : null}
  </div>`;
}

/**
 * Completion receipt — consequential writes end here, not only a toast.
 * Soft≠Live: only paint fields the caller proved.
 */
export function Receipt({
  title,
  accepted,
  effective,
  evidence,
  at,
  next,
  tone = "go",
}) {
  return html`<aside className="layer-receipt" data-tone=${tone} aria-live="polite">
    <header>
      <span className="layer-receipt__mark">Receipt</span>
      <b>${title || "Done"}</b>
    </header>
    <dl>
      ${accepted != null ? html`<div><dt>Accepted</dt><dd>${accepted}</dd></div>` : null}
      ${effective != null ? html`<div><dt>Effective</dt><dd>${effective}</dd></div>` : null}
      ${evidence != null ? html`<div><dt>Evidence</dt><dd>${evidence}</dd></div>` : null}
      ${at != null ? html`<div><dt>When</dt><dd>${at}</dd></div>` : null}
    </dl>
    ${next ? html`<p className="layer-receipt__next">${next}</p>` : null}
  </aside>`;
}

/**
 * Searchable picker for large registries (boards, devices, people, models).
 * Soft≠ invent rows — callers pass Live lists only.
 */
export function SearchPicker({
  items = [],
  value = "",
  onChange,
  placeholder = "Search…",
  label = "Pick",
  emptySay = "Nothing matches.",
  disabled = false,
  maxPaint = 80,
  getId = (row) => String(row.id || row.value || row.device_id || ""),
  getLabel = (row) => String(row.label || row.name || row.device_id || row.id || ""),
  getHint = (row) => String(row.hint || row.status || row.meta || ""),
  getStatus = (row) => (row.status_on == null ? null : !!row.status_on),
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const root = useRef(null);
  const rows = Array.isArray(items) ? items : [];
  const selected = rows.find((r) => getId(r) === String(value));
  const filtered = useMemo(() => {
    const needle = String(q || "").trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const hay = (getLabel(r) + " " + getId(r) + " " + getHint(r)).toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q]);
  const paint = filtered.slice(0, Math.max(12, maxPaint | 0));
  const more = Math.max(0, filtered.length - paint.length);

  useEffect(() => { setHi(0); }, [q, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (root.current && !root.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const t = setTimeout(() => {
      addEventListener("pointerdown", onDoc, true);
      addEventListener("keydown", onKey);
    }, 0);
    return () => {
      clearTimeout(t);
      removeEventListener("pointerdown", onDoc, true);
      removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (id) => {
    setOpen(false);
    setQ("");
    setHi(0);
    if (onChange) onChange(id);
  };

  const onFilterKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHi((n) => Math.min(paint.length - 1, n + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((n) => Math.max(0, n - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = paint[hi];
      if (row) pick(getId(row));
    }
  };

  return html`<div className="layer-picker" ref=${root} data-open=${open ? "1" : "0"}>
    <button type="button" className="layer-picker__face inp" disabled=${disabled}
      aria-label=${label} aria-expanded=${open ? "true" : "false"}
      onClick=${() => !disabled && setOpen((v) => !v)}>
      <span className=${selected ? "" : "layer-picker__ph"}>
        ${selected ? getLabel(selected) : (placeholder || "Select…")}
      </span>
      <span className="craft-sel__caret" aria-hidden="true"/>
    </button>
    ${open ? html`<div className="layer-picker__menu" role="listbox" aria-label=${label}>
      <input className="inp layer-picker__q" value=${q} autofocus
        aria-label=${"Filter " + label}
        placeholder=${"Filter " + rows.length + "…"}
        onInput=${(e) => setQ(e.target.value)}
        onKeyDown=${onFilterKey}/>
      <p className="layer-picker__count">${filtered.length} match${filtered.length === 1 ? "" : "es"}</p>
      <ul>
        ${paint.map((r, i) => {
          const id = getId(r);
          const on = id === String(value);
          const st = getStatus(r);
          return html`<li key=${id}>
            <button type="button" role="option" aria-selected=${on ? "true" : "false"}
              data-on=${on ? "1" : "0"} data-hi=${i === hi ? "1" : "0"} onClick=${() => pick(id)}>
              <b>${getLabel(r)}</b>
              <em>${getHint(r)}</em>
              ${st != null ? html`<i data-on=${st ? "1" : "0"}>${st ? "ready" : "gap"}</i>` : null}
            </button>
          </li>`;
        })}
      </ul>
      ${!paint.length ? html`<p className="note">${emptySay}</p>` : null}
      ${more ? html`<p className="note">+${more} more — refine the search.</p>` : null}
    </div>` : null}
  </div>`;
}

/** Layer-1 task strip: now / next / one primary action. */
export function TaskStrip({ now, next, actionLabel, onAction, disabled }) {
  return html`<${M.section} className="layer-task" aria-label="What to do now"
    initial=${{ opacity: 0, y: 8 }} animate=${{ opacity: 1, y: 0 }} transition=${SOFT}>
    <div>
      <p className="eyebrow">Now</p>
      <b>${now || "—"}</b>
      ${next ? html`<p>${next}</p>` : null}
    </div>
    ${actionLabel && onAction ? html`
      <button type="button" className="btn btn--sm" disabled=${!!disabled} onClick=${onAction}>
        ${actionLabel}
      </button>` : null}
  <//>`;
}

export default {
  READINESS_LABELS,
  normalizeReadinessSteps,
  ReadinessState,
  TechnicalDetails,
  EmptyAction,
  Receipt,
  SearchPicker,
  TaskStrip,
};
